/*
    DEFAULT LIMITS = isDefaultLimit: true && isUserLimit: false
    CUSTOM LIMITS = isDefaultLimit: false && isUserLimit: false
    USER/PERSONAL LIMITS = isDefaultLimit: false && isUserLimit: true
*/
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { omit, some, uniq } from 'lodash';
import { Link, useHistory } from 'react-router-dom';
import useUserCapability from 'sharedHooks/useUserCapability';
import { Expandable, GeneralButton, LoadingStatusWrapper, SearchBox } from 'sharedComponents';
import { translate } from 'helpers/translateHelper';
import { IconCaretRight, IconDropDownMo, IconLeftArrow, IconPlus } from 'svgIcons/MotionPortalIcons';
import LimitTitle from './components/LimitTitle';
import { addSignal, resetState, cancel, postLimitRequest } from './actions';
import { WINDOW_EVENTS } from './components/constants';
import SuggestLimitModal from './components/SuggestLimitModal';
import { routes } from 'routes';
import NotificationBanner from './components/NotificationBanner';
import { isSmSeAsset } from '../helpers';
import colors from 'theme/_colors.scss';
import { apiCallStatus, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import UnSavedChangesModal from './components/UnSavedChangesModal';
import LimitApplicable from './components/LimitApplicable';
import LimitNotApplicable from './components/LimitNotApplicable';
import { NON_APPLICABLE_TIMESERIES_KEYS } from './constants';

const getPayload = (timeSeries, checkIsSmseAsset) => {
    const DESCRIPTION_KEY = 'description';
    const filteredTimeSeriesWithModifiedLimits = timeSeries
        ?.filter((item) => item.isModified)
        .map((item) => {
            const resetToDefault = item.limits?.some((lmt) => lmt?.resetToDefault) || false;
            const modifiedLimits = item.limits?.filter((limit) => limit?.isModified || limit?.resetToDefault || !limit?.isDefault) || [];

            return {
                ...omit(item, [
                    'isModified',
                    'uniqId',
                    'isNew',
                    'unit',
                    'displayName',
                    'displayNameTextKey',
                    'appendCodeToTranslation',
                    'isOffsetEnabled',
                    'isDefaultLimitEditable',
                    'isAddingCustomLimitsAllowed',
                    'isHealthTimeseries',
                    'minValue',
                    'maxValue',
                ]),
                resetToDefault, // set this flag to true if user selects Reset to default from menu
                limits: modifiedLimits.map((limit) => {
                    const payload = [
                        'uniqId',
                        'isError',
                        'isModified',
                        'isSameLimit',
                        'isDefault',
                        'limitType',
                        'referenceTimeseriesKey',
                        'isBasedOnDefault',
                    ];
                    const limitForPayload = limit?.isModified && limit?.isDefault && !checkIsSmseAsset
                        ? { ...limit, description: '' }
                        : limit;

                    if (limitForPayload?.description === null || limitForPayload?.description === undefined) {
                        payload.push(DESCRIPTION_KEY);
                    }

                    return omit(limitForPayload, payload);
                }),
            };
        });
    return filteredTimeSeriesWithModifiedLimits;
};


const getSearchMatcher = (search) => {
    const searchToLower = search?.toString()?.toLowerCase();
    return (value) => value?.toString()?.toLowerCase()?.includes(searchToLower);
};

const LimitConfigComponent = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const userCapability = useUserCapability();
    const { model, asset, actions } = props;
    const [isDisableSave, setIsDisableSave] = useState(false);
    const [isDisableCancel, setIsDisableCancel] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showNotifyMeMessage, setShowNotifyMeMessage] = useState(true);
    const [showMaxLimitMessage, setShowMaxLimitMessage] = useState(true);
    const [nextLocation, setNextLocation] = useState(null);
    const [contextMenuId, setContextMenuId] = useState(null);
    const [expandedItem, setExpandedItem] = useState(model?.expandedSignals || []);
    const [expandedItemFromChart, setExpandedItemFromChart] = useState(false);
    // const [showOffset, setShowOffset] = useState(false);
    const unblockRef = useRef(null);
    const checkIsSmseAsset = isSmSeAsset(asset);
    const timeseriesLoading = model?.timeSeries?.isLoading === apiCallStatus.LOADING;
    const hasModifiedItem = model.currentTimeSeries?.some(item =>
        item?.isModified || item?.limits?.some(limit => limit?.isModified)
    );
    const resetToDefaultLimitCapability = userCapability.hasOperationalParametersEditDefault();
    useEffect(() => {
        setExpandedItem(uniq(model?.expandedSignals) || []);
    }, [model?.expandedSignals]);

    useEffect(() => {
        if (hasModifiedItem) {
            const handleBeforeUnload = (event) => {
                event.preventDefault();
                event.returnValue = '';
            };

            unblockRef.current = history.block((location) => {
                if (hasModifiedItem) {
                    setNextLocation(location);
                    setShowModal(true);
                    return false;
                }
                return true;
            });

            window.addEventListener(WINDOW_EVENTS.BEFORE_UNLOAD, handleBeforeUnload);

            return () => {
                window.removeEventListener(WINDOW_EVENTS.BEFORE_UNLOAD, handleBeforeUnload);
                if (unblockRef.current) { unblockRef.current(); }
            };
        }
    }, [hasModifiedItem, history]);

    useEffect(() => {
        if (timeseriesLoading) {
            setIsDisableSave(true);
            setIsDisableCancel(true);
        } else {
            setIsDisableCancel(false);
            const hasModifiedItemWithoutDisplayName = model?.currentTimeSeries?.some(item =>
                item?.isModified && (!item?.displayName || item?.displayName?.length === 0)
            );

            const hasInvalidLimits = model.currentTimeSeries.filter(item => item.isModified).some(item =>
                some(item.limits, limit =>
                    !limit.limitLevel || limit.limitValue === undefined || limit.limitValue === null || limit.limitValue === '' || !limit.limitDirection || limit.isSameLimit
                )
            );
            // COMMENTED FOR ENABLE SAVE WITHOUT LIMITS
            const hasNoLimitsAndOffset = model?.currentTimeSeries?.filter(item => item?.limits?.length === 0 && (item?.offset === null || item?.offset === undefined || item?.offset === ''))?.length > 0;

            const hasModifiedSignals = model?.currentTimeSeries?.filter(item => item?.isModified);
            if (hasModifiedSignals?.length) {
                if (hasModifiedItemWithoutDisplayName || hasInvalidLimits || hasNoLimitsAndOffset) {
                    setIsDisableSave(true);
                } else {
                    setIsDisableSave(false);
                }
            } else {
                setIsDisableCancel(true);
                setIsDisableSave(true);
            }
        }
        const hasNewLimits = model?.currentTimeSeries?.filter(item => item?.isNew);
        if (hasNewLimits?.length) {
            setIsDisableCancel(false);
        }
    }, [model.currentTimeSeries]);

    useEffect(() => {
        // COMMENTED FOR UPDATE UNIT CONVERSION
        // if (asset?.id === model?.timeSeries?.assetId) {
        //     return;
        // }

        dispatch(resetState());
        actions.getLimitKPI(asset?.id);
        actions.getTimeSeries(asset?.id);

        // Clear stale error state on unmount to prevent error boundary from re-triggering on retry.
        return () => dispatch(resetState());
    }, [asset?.id]);

    const handleAddSignal = () => {
        const newSignal = {
            uniqId: crypto.randomUUID(),
            limits: [],
            isModified: true,
            isNew: true,
        };
        dispatch(addSignal(newSignal));
    };

    const handleCancel = () => {
        dispatch(cancel());
        const newSignal = model?.currentTimeSeries?.find((cts) => cts?.isNew);
        if (newSignal) {
            actions.setExpandedSignals(expandedItem.filter((id) => newSignal?.displayNameTextKey !== id && `P_${newSignal?.displayNameTextKey}` !== id));
        }
    };

    const handleSaveConfig = () => {
        const payload = getPayload(model?.currentTimeSeries, checkIsSmseAsset);
        dispatch(postLimitRequest(asset?.id, payload));
    };

    const filteredList = useMemo(() => {
        const isSearchMatch = getSearchMatcher(searchValue);

        const timeseries = model?.currentTimeSeries ?? [];

        return searchValue
            ? timeseries?.filter((item) =>
                isSearchMatch(item?.displayName) ||
                isSearchMatch(item?.timeseriesKey) ||
                isSearchMatch(item?.trendKey)
            )
            : timeseries;
    }, [searchValue, model.currentTimeSeries]);

    const displayNameTextKeys = new Set(model?.currentTimeSeries?.map(item => item?.displayNameTextKey));

    const updatedConfigList = model?.limitKPI?.kpiList?.flatMap(configItem =>
        configItem?.timeseries?.filter(series => !displayNameTextKeys?.has(series?.timeseriesNameTextKey))
    );

    const hasEditableLimit = updatedConfigList?.length > 0;

    const handleSuggestLimitModal = (signal) => {
        actions.handleSelectTimeSeriesForSuggestion(!model.showSuggestLimitModal ? signal : null);
        actions.handleShowSuggestLimitModal(!model.showSuggestLimitModal);
        if (signal) {
            actions.getSelectedTimeSeriesSuggestedData({ assetId: asset.id, timeseriesKey: signal?.timeseriesKey });
        }
    };

    const handleDiscardAndContinue = () => {
        setShowModal(false);
        handleCancel();
        if (nextLocation) {
            if (unblockRef.current) { unblockRef.current(); }
            history.push(`${nextLocation?.pathname}${nextLocation?.search}`);
        }
        actions.setShowLimitSetting(false);
    };

    const handleExpandSignal = (signalUniqId) => {
        let expandedSignals = [...expandedItem];
        if (expandedSignals?.includes(signalUniqId)) {
            expandedSignals = expandedItem.filter((id) => signalUniqId !== id && `P_${signalUniqId}` !== id);
        } else {
            expandedSignals?.push(signalUniqId);
            expandedSignals?.push(`P_${signalUniqId}`);
        }
        actions.setExpandedSignals(uniq(expandedSignals));
    };

    const handlePreSelectedLimits = () => {
        if (model?.selectedTrendKPIs?.length && model.currentTimeSeries?.length || model?.clickedKpiConfig) {
            const selectedSignal = model.currentTimeSeries?.filter((fl) => fl?.isTrend ? model?.selectedTrendKPIs?.includes(fl?.trendKey) || model?.clickedKpiConfig === fl?.trendKey : model?.selectedTrendKPIs?.includes(fl?.timeseriesKey) || model?.clickedKpiConfig === fl?.timeseriesKey);
            if (selectedSignal?.length && !timeseriesLoading) {
                selectedSignal?.map(ss => {
                    const signal = model.currentTimeSeries?.find((fl) => checkIsSmseAsset ? ss?.timeseriesKey === fl?.timeseriesKey : ss?.timeseriesKey === fl?.timeseriesKey && ss?.trendKey === fl?.trendKey);
                    if (signal) {
                        const expandItemName = signal?.displayNameTextKey;
                        setExpandedItem((oldState) => [...oldState, ...expandedItem, expandItemName, `P_${expandItemName}`]);
                    }
                });
                // SMOOTH SCROLL AFTER UI LOAD
                const signal = selectedSignal[0];
                const section = document.getElementById(`${signal?.timeseriesKey}`);
                section?.scrollIntoView({ behavior: 'smooth' });
                setExpandedItemFromChart(true);
            }
        }
    };

    useEffect(() => {
        if (!expandedItemFromChart) {
            handlePreSelectedLimits();
        }
    }, [model?.selectedTrendKPIs?.length, model.timeSeries?.isLoading, model?.clickedKpiConfig]);

    const handleOffsetModal = (id) => {
        actions.setOffsetModalId(id);
    };

    return (
        <div className='limit-config-container'>
            {showModal &&
                <UnSavedChangesModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    handleDiscardAndContinue={handleDiscardAndContinue}
                    isResetToDefault={false}
                />
            }
            <div className='limit-config-header'>
                <div className='header-left'>
                    <GeneralButton
                        type='discreet'
                        className='limit-btn limit-back-btn ps-0'
                        icon={<IconLeftArrow color={colors.black} />}
                        text={translate('ABB.Powertrain.Frontend.backButton')}
                        onClick={() => {
                            if (hasModifiedItem) { setShowModal(true); return; }
                            actions.setShowLimitSetting(false);
                            actions.setClickedConfigKpi(null);
                            actions.setExpandedSignals([]);
                            setExpandedItemFromChart(false);
                        }}
                    />
                    <SearchBox
                        className='limit-search-box'
                        value={searchValue}
                        onChange={(value) => setSearchValue(value)}
                        placeholder={translate('ABB.Powertrain.Frontend.searchBySignalName')}
                        maxLength={MAX_INPUT_CHAR_LENGTH}
                    />
                    {filteredList && filteredList?.length > 0 &&
                        <div className='limit-count'>
                            {filteredList?.length} {filteredList?.length > 1 ? translate('ABB.Powertrain.Frontend.signals') : translate('ABB.Powertrain.Frontend.signal')}
                        </div>
                    }
                    {/* Needed for future use case */}
                    {/* <GeneralButton
                        type='normal'
                        text={translate('ABB.Powertrain.Frontend.expandAll')}
                        className='limit-btn'
                        onClick={() => setIsExpanded(!isExpanded)}
                        disabled={model.timeSeries?.isLoading}
                    /> */}
                </div>
                <div className='header-right'>

                    <GeneralButton
                        type='normal'
                        className='limit-config-Cancel-button'
                        text={translate('ABB.Powertrain.Frontend.generalFilterCancelButton')}
                        onClick={handleCancel}
                        disabled={isDisableCancel || timeseriesLoading}
                    />
                    <GeneralButton
                        disabled={isDisableSave || timeseriesLoading}
                        style={{ backgroundColor: '#9CBDF7 !important' }}
                        type='primary'
                        className='limit-config-save-button'
                        text={translate('ABB.Powertrain.Frontend.saveButton')}
                        onClick={handleSaveConfig}
                    />
                    <GeneralButton
                        type='primary'
                        text={translate('ABB.Powertrain.Frontend.addSignal')}
                        icon={<IconPlus color={'#ffffff'} />}
                        className='limit-btn'
                        onClick={handleAddSignal}
                        disabled={timeseriesLoading || !hasEditableLimit}
                    />

                </div>
                {showNotifyMeMessage && <NotificationBanner
                    message={<div>
                        {translate('ABB.Powertrain.Frontend.limitsNotifyMeMessage')} {' '}
                        <Link
                            to={{ pathname: routes.NotificationSettings }}
                        >
                            {translate('ABB.Powertrain.Frontend.userProfileUserSettingsLabel')} - {translate('ABB.Powertrain.Frontend.userProfileNotificationSettings')}
                        </Link>
                    </div>}
                    onClose={() => { setShowNotifyMeMessage(false); }}
                    type='notification'
                />}
            </div>
            <LoadingStatusWrapper loadingStatus={[model?.timeSeries?.isLoading]} >
                <div className='signal-limit-container'>
                    {(filteredList ?? []).map(timeSeries => {
                        const defaultLimits = timeSeries?.limits?.filter((limit) => !limit?.isUserLimit); // ALSO INCLUDE CUSTOM LIMITS
                        const userLimits = timeSeries?.limits?.filter((limit) => limit?.isUserLimit); // ONLY USER LIMITS (PERSONAL LIMITS)
                        const notificationCount = defaultLimits?.filter((limit) => limit?.notificationEnabled)?.length;
                        const anyLimitEventActive = timeSeries?.limits?.find((limit) => limit?.eventEnabled);
                        const expandItemName = timeSeries?.displayNameTextKey;
                        const expanded = expandedItem?.includes(expandItemName);
                        const isApplicable = !NON_APPLICABLE_TIMESERIES_KEYS.includes(timeSeries?.timeseriesKey);
                        return <Expandable
                            disabled={!timeSeries?.displayName}
                            open={!timeSeries?.displayName ? false : isExpanded || expanded}
                            key={expandItemName}
                            id={timeSeries?.timeseriesKey}
                            headerClassName={expanded ? 'expanded' : ''}
                            onClick={() => { handleExpandSignal(expandItemName); }}
                            collapsedIcon={<IconCaretRight />}
                            expandedIcon={<IconDropDownMo />}
                            title={
                                <LimitTitle
                                    {...props}
                                    item={timeSeries}
                                    setExpandedItem={actions.setExpandedSignals}
                                    expandedItem={expandedItem}
                                    handleExpandSignal={handleExpandSignal}
                                    isExpanded={isExpanded}
                                    setIsExpanded={setIsExpanded}
                                    openSuggestLimitModal={() => handleSuggestLimitModal(timeSeries)}
                                    isUserLimit={false}
                                    showDiscard={defaultLimits?.filter((l) => l?.isModified)?.length > 0}
                                    anyLimitEventActive={anyLimitEventActive}
                                    notificationCount={notificationCount}
                                    setShowMaxLimitMessage={setShowMaxLimitMessage}
                                    defaultLimits={defaultLimits}
                                    checkIsSmseAsset={checkIsSmseAsset}
                                    hasModifiedItem={hasModifiedItem}
                                    model={model}
                                    contextMenuId={contextMenuId}
                                    setContextMenuId={setContextMenuId}
                                    timeseriesLoading={timeseriesLoading}
                                    expandItemName={expandItemName}
                                    defaultLimitValues={model?.defaultLimits?.timeseries}
                                    defaultLimtsLoading={model?.defaultLimtsLoading}
                                    resetToDefaultLimitCapability={resetToDefaultLimitCapability}
                                    isAnySignalModified={!isDisableSave}
                                    showOffset={model.offsetModalId}
                                    setShowOffset={handleOffsetModal}
                                    isApplicable={isApplicable}
                                />
                            }
                        >
                            {/* ANOTHER LOGIC FOR CORNER CASE */}
                            {/* {NON_APPLICABLE_TIMESERIES_KEYS?.includes(timeSeries?.timeseriesKey) */}
                            {!isApplicable && checkIsSmseAsset
                                ? <LimitNotApplicable {...props} timeSeries={timeSeries} setOffsetModalId={handleOffsetModal} /> :
                                <LimitApplicable
                                    {...props}
                                    timeSeries={timeSeries}
                                    isExpanded={isExpanded}
                                    expandedItem={expandedItem}
                                    setExpandedItem={actions.setExpandedSignals}
                                    setIsExpanded={setIsExpanded}
                                    userLimits={userLimits}
                                    checkIsSmseAsset={checkIsSmseAsset}
                                    handleExpandSignal={handleExpandSignal}
                                    expandItemName={expandItemName}
                                    setContextMenuId={setContextMenuId}
                                    defaultLimits={defaultLimits}
                                    translate={translate}
                                    asset={asset}
                                    showMaxLimitMessage={showMaxLimitMessage}
                                    setShowMaxLimitMessage={setShowMaxLimitMessage} />
                            }
                        </Expandable>;
                    })}
                </div>
            </LoadingStatusWrapper>
            {model.showSuggestLimitModal &&
                <SuggestLimitModal {...props} close={handleSuggestLimitModal} />
            }
        </div>
    );
};

LimitConfigComponent.propTypes = {
    model: PropTypes.shape({
        expandedSignals: PropTypes.arrayOf(PropTypes.string),
        timeSeries: PropTypes.shape({
            isLoading: PropTypes.string,
            assetId: PropTypes.string,
        }),
        currentTimeSeries: PropTypes.arrayOf(
            PropTypes.shape({
                isModified: PropTypes.bool,
                limits: PropTypes.arrayOf(
                    PropTypes.shape({
                        isUserLimit: PropTypes.bool,
                        limitLevel: PropTypes.string,
                        limitValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                        limitDirection: PropTypes.string,
                        isSameLimit: PropTypes.bool,
                    })
                ),
                displayName: PropTypes.string,
                displayNameTextKey: PropTypes.string,
                timeseriesKey: PropTypes.string,
                trendKey: PropTypes.string,
                isNew: PropTypes.bool,
            })
        ),
        limitKPI: PropTypes.shape({
            kpiList: PropTypes.arrayOf(
                PropTypes.shape({
                    timeseries: PropTypes.arrayOf(
                        PropTypes.shape({
                            timeseriesNameTextKey: PropTypes.string,
                        })
                    ),
                })
            ),
        }),
        selectedTrendKPIs: PropTypes.arrayOf(PropTypes.string),
        clickedKpiConfig: PropTypes.string,
        showSuggestLimitModal: PropTypes.bool,
        defaultLimits: PropTypes.shape({
            timeseries: PropTypes.array,
        }),
        defaultLimtsLoading: PropTypes.bool,
        offsetModalId: PropTypes.any,
    }),
    asset: PropTypes.shape({
        id: PropTypes.string,
    }),
    actions: PropTypes.shape({
        getLimitKPI: PropTypes.func,
        getTimeSeries: PropTypes.func,
        setExpandedSignals: PropTypes.func,
        setShowLimitSetting: PropTypes.func,
        setClickedConfigKpi: PropTypes.func,
        handleSelectTimeSeriesForSuggestion: PropTypes.func,
        handleShowSuggestLimitModal: PropTypes.func,
        getSelectedTimeSeriesSuggestedData: PropTypes.func,
        setOffsetModalId: PropTypes.func,
    }),
};

export default React.memo(LimitConfigComponent);
